/** @format */

import type { RegenerationDecision } from "../../store/useReviewCommitStore";

export type StepNavStatus =
  | "locked"
  | "none"
  | "accepted"
  | "changes_requested"
  | "pending_review"
  /** Super-admin only: waiting for owner to review — neutral lock. Not used while admin is still preparing the proposal. */
  | "super_admin_preview";

/**
 * Owner: review colors from decisions / pending.
 * Super-admin with candidate: amber “proposal” styling while they prepare or edit; neutral lock only when
 * `superAdminWaitingForOwnerReview` (regeneration surfaced to owner, admin waits on their review).
 */
export function getStepNavStatus(args: {
  hasCandidate: boolean;
  isLocked: boolean;
  reviewMode: boolean;
  decision: RegenerationDecision | undefined;
  viewerIsSuperAdmin: boolean;
  /** True when super-admin is blocked waiting on owner review (not while generating/editing the proposal). */
  superAdminWaitingForOwnerReview: boolean;
}): StepNavStatus {
  const {
    hasCandidate,
    isLocked,
    reviewMode,
    decision,
    viewerIsSuperAdmin,
    superAdminWaitingForOwnerReview,
  } = args;

  if (hasCandidate) {
    if (viewerIsSuperAdmin) {
      if (superAdminWaitingForOwnerReview) return "super_admin_preview";
      return "pending_review";
    }
    if (reviewMode && decision === "accept") return "accepted";
    if (reviewMode && decision === "request_change") return "changes_requested";
    return "pending_review";
  }

  if (isLocked) return "locked";
  return "none";
}

/** CSS modifier suffix for `sop-editor-sidebar-step-num--status-*` and `sop-editor-step-badge--*` */
export function stepNavStatusToSuffix(status: StepNavStatus): string {
  switch (status) {
    case "changes_requested":
      return "changes";
    case "pending_review":
      return "pending";
    case "super_admin_preview":
      return "admin_preview";
    default:
      return status;
  }
}

export function getStepNavStatusTitle(status: StepNavStatus): string | undefined {
  switch (status) {
    case "locked":
      return "Locked — update in progress";
    case "super_admin_preview":
      return "Waiting for owner review — proposal sent";
    case "pending_review":
      return "AI proposal available — awaiting your review";
    case "accepted":
      return "Accepted";
    case "changes_requested":
      return "Changes requested";
    case "none":
    default:
      return undefined;
  }
}

export function getStepNavStatusAriaLabel(
  status: StepNavStatus,
  stepTitle: string,
  stepNumber: number,
): string {
  const base = `Step ${stepNumber}: ${stepTitle}`;
  const extra = getStepNavStatusTitle(status);
  return extra ? `${base}. ${extra}` : base;
}
